import { useState } from 'react';
import supabase from '../lib/supabase';

function GeometryFigure({ figure }) {
  if (!figure) return null;

  if (figure.shape === 'right_triangle') {
    const { a, b, c } = figure.sides || {};
    return (
      <svg className="geo-figure" viewBox="0 0 220 160" aria-label="Right triangle diagram">
        {/* triangle: right angle at bottom-left */}
        <polygon points="30,130 170,130 30,20" fill="rgba(99,102,241,0.1)" stroke="#6366f1" strokeWidth="2" />
        {/* right-angle marker */}
        <polyline points="30,110 50,110 50,130" fill="none" stroke="#6366f1" strokeWidth="1.5" />
        {/* side a — vertical (left) */}
        {a && <text x="14" y="82" className="geo-label">{a}</text>}
        {/* side b — horizontal (bottom) */}
        {b && <text x="90" y="150" className="geo-label">{b}</text>}
        {/* side c — hypotenuse */}
        {c && <text x="108" y="65" className="geo-label" transform="rotate(-35,108,65)">{c}</text>}
      </svg>
    );
  }

  if (figure.shape === 'rectangle') {
    const { w, h } = figure.dims || {};
    return (
      <svg className="geo-figure" viewBox="0 0 220 160" aria-label="Rectangle diagram">
        <rect x="30" y="30" width="160" height="100" fill="rgba(99,102,241,0.1)" stroke="#6366f1" strokeWidth="2" />
        {/* width label — top */}
        {w && <text x="110" y="22" className="geo-label" textAnchor="middle">{w}</text>}
        {/* height label — right */}
        {h && <text x="200" y="85" className="geo-label" textAnchor="start">{h}</text>}
      </svg>
    );
  }

  if (figure.shape === 'circle') {
    const { r } = figure;
    return (
      <svg className="geo-figure" viewBox="0 0 220 160" aria-label="Circle diagram">
        <circle cx="110" cy="80" r="65" fill="rgba(99,102,241,0.1)" stroke="#6366f1" strokeWidth="2" />
        {/* radius line */}
        <line x1="110" y1="80" x2="175" y2="80" stroke="#6366f1" strokeWidth="1.5" strokeDasharray="4 3" />
        {r && <text x="138" y="72" className="geo-label">{r}</text>}
      </svg>
    );
  }

  return null;
}

// Renders question text — converts markdown tables into real HTML tables
export function QuestionText({ text }) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    // Detect start of a markdown table (contains | and not just dashes)
    if (line.includes('|') && !/^[\s|:-]+$/.test(line)) {
      const tableLines = [];
      while (i < lines.length && lines[i].includes('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      // Remove separator row (---|--- style)
      const rows = tableLines.filter(l => !/^[\s|:-]+$/.test(l));
      const headers = rows[0].split('|').map(c => c.trim()).filter(Boolean);
      const body = rows.slice(1);
      elements.push(
        <div key={i} className="q-table-wrap">
          <table className="q-table">
            <thead>
              <tr>{headers.map((h, j) => <th key={j}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {body.map((row, j) => (
                <tr key={j}>
                  {row.split('|').map(c => c.trim()).filter(Boolean).map((cell, k) => (
                    <td key={k}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else {
      if (line.trim()) {
        elements.push(<p key={i} className="q-text">{line}</p>);
      }
      i++;
    }
  }

  return <>{elements}</>;
}

function FlagButton({ question }) {
  const [flagged, setFlagged] = useState(false);
  async function handleFlag() {
    setFlagged(true);
    const { data: { session } } = await supabase.auth.getSession();
    await supabase.from('flagged_questions').insert({
      user_id: session?.user?.id ?? null,
      subject: question.subject ?? null,
      topic: question.concept ?? null,
      question: question.question,
      options: question.options,
      answer: question.answer,
      explanation: question.explanation ?? null,
    });
  }
  return (
    <button
      className="flag-btn"
      onClick={handleFlag}
      disabled={flagged}
      title="Report an issue with this question"
    >
      {flagged ? '⚑ Reported — thanks!' : '⚑ Report incorrect answer'}
    </button>
  );
}

function QuizQuestion({ question, color, onAnswer, onAskAI }) {
  const [selected, setSelected] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const answered = selected !== null;

  function pick(option) {
    if (answered) return;
    setSelected(option);
    onAnswer(option === question.answer);
  }

  function optionStyle(option) {
    if (!answered) return {};
    if (option === question.answer) return { background: '#4caf50', color: '#fff', borderColor: '#4caf50' };
    if (option === selected) return { background: '#e53935', color: '#fff', borderColor: '#e53935' };
    return { opacity: 0.5 };
  }

  return (
    <div className="quiz-question">
      <div className="q-meta">
        <span className="q-concept">{question.concept}</span>
        <span className={`q-diff q-diff--${question.difficulty}`}>{question.difficulty}</span>
      </div>

      <QuestionText text={question.question} />
      <GeometryFigure figure={question.figure} />

      <div className="q-options">
        {question.options.map((option, i) => (
          <button
            key={i}
            className="q-option"
            style={optionStyle(option)}
            onClick={() => pick(option)}
          >
            <span className="q-option-letter">{['A','B','C','D'][i]}</span>
            {option}
          </button>
        ))}
      </div>

      {!answered && (
        <button className="hint-btn" onClick={() => setShowHint(h => !h)}>
          {showHint ? 'Hide Hint' : 'Show Hint'}
        </button>
      )}
      {showHint && !answered && (
        <p className="hint-text">{question.hint}</p>
      )}

      {answered && (
        <div className="explanation-box">
          <p className="explanation-text">{question.explanation}</p>
          <button className="ask-ai-btn" style={{ background: color }} onClick={onAskAI}>
            Ask AI Tutor about this
          </button>
          <FlagButton question={question} />
        </div>
      )}
    </div>
  );
}

export { GeometryFigure };
export default QuizQuestion;
